import { Alpha2CountryCode, Country } from '@getpopsure/public-models';
import dayjs, { ManipulateType } from 'dayjs';
import { isCountry } from '../utils/typeGuards';

export { runValidation } from './runValidation';

const greaterThan = {
  fn: <T>(a: T, b: T) => {
    return a > b;
  },
  msg: (msg: string, value: number, comparisonValue: number) => {
    const replacements: Record<string, number> = {
      '{{value}}': value,
      '{{comparisonValue}}': comparisonValue,
    };

    return msg.replace(/{{\w+}}/g, (key) => {
      return `${replacements[key]}` || key;
    });
  },
};

const lessThan = {
  fn: <T>(a: T, b: T) => {
    return a < b;
  },
  msg: (msg: string, value: number, comparisonValue: number) => {
    const replacements: Record<string, number> = {
      '{{value}}': value,
      '{{comparisonValue}}': comparisonValue,
    };

    return msg.replace(/{{\w+}}/g, (key) => {
      return `${replacements[key]}` || key;
    });
  },
};

const dateIsInFuture = {
  fn: (date: string, compareDate: string) => {
    return dayjs(compareDate).isBefore(date);
  },
  msg: (msg: string) => {
    const DATE_FORMAT = 'DD MMM YYYY';
    return msg.replace(/{{\w+}}/g, dayjs().add(1, 'day').format(DATE_FORMAT));
  },
};

const dateIsInPast = {
  fn: (date: string, compareDate: string) => {
    return dayjs(compareDate).isAfter(date);
  },
  msg: (msg: string) => {
    const DATE_FORMAT = 'DD MMM YYYY';
    return msg.replace(/{{\w+}}/g, dayjs().add(1, 'day').format(DATE_FORMAT));
  },
};

const dateDiff = {
  fn: (
    date: string,
    value: number,
    compare: { type: ManipulateType; dir?: 'gt' | 'lt' },
    comparisonValue: string | undefined = undefined
  ) => {
    const baseValue = dayjs(comparisonValue).diff(dayjs(date), compare.type);

    if (compare.dir === 'lt') {
      return baseValue <= value;
    }
    return baseValue >= value;
  },
  msg: (msg: string) => {
    return msg;
  },
};

const equals = {
  fn: <T extends unknown>(a: T, b: T) => {
    return a === b;
  },
  msg: <T>(msg: string, value: T, comparisonValue: T) => {
    const replacements: Record<string, T> = {
      '{{value}}': value,
      '{{comparisonValue}}': comparisonValue,
    };

    return msg.replace(/{{\w+}}/g, (key) => {
      return `${replacements[key]}` || key;
    });
  },
};

const notEquals = {
  fn: <T extends unknown>(a: T, b: T) => {
    return !equals.fn(a, b);
  },
  msg: <T>(msg: string, value: T, comparisonValue: T) => {
    return equals.msg(msg, value, comparisonValue);
  },
};

const same = {
  fn: <T extends unknown>(a: T, b: T) => {
    // eslint-disable-next-line eqeqeq
    return a == b;
  },
  msg: <T>(msg: string, value: T, comparisonValue: T) => {
    const replacements: Record<string, T> = {
      '{{value}}': value,
      '{{comparisonValue}}': comparisonValue,
    };

    return msg.replace(/{{\w+}}/g, (key) => {
      return `${replacements[key]}` || key;
    });
  },
};

const notSame = {
  fn: <T extends unknown>(a: T, b: T) => {
    return !same.fn(a, b);
  },
  msg: <T>(msg: string, value: T, comparisonValue: T) => {
    return same.msg(msg, value, comparisonValue);
  },
};

const isIn = {
  fn: <T extends unknown>(a: T, b: T[]) => {
    return b.includes(a);
  },
  msg: <T>(msg: string, value: T, comparisonValue: T[]) => {
    const comparisonValueReplacement: string = String(
      `[${comparisonValue.join(', ')}]`
    );
    const replacements: Record<string, string> = {
      '{{value}}': String(value),
      '{{comparisonValue}}': comparisonValueReplacement,
    };

    return msg.replace(/{{\w+}}/g, (key) => {
      return `${replacements[key]}` || key;
    });
  },
};

const isNotIn = {
  fn: <T extends unknown>(a: T, b: T[]) => {
    return !isIn.fn(a, b);
  },
  msg: <T>(msg: string, value: T, comparisonValue: T[]) => {
    return isIn.msg(msg, value, comparisonValue);
  },
};

const doCountryCodesMatch = {
  fn: (a: Country | Country[], b: Alpha2CountryCode[]) => {
    if (!a) {
      return false;
    }

    if (Array.isArray(a)) {
      return a
        .filter((country) => isCountry(country))
        .map(({ code }) => code)
        .some((countryCode) => {
          return b.includes(countryCode);
        });
    }

    if (isCountry(a)) {
      return b.includes(a.code);
    }

    return false;
  },

  // TODO: [KONG] Update msg func to return something meaningful
  msg: () => {
    return '';
  },
};

const doCountryCodesNotMatch = {
  fn: (a: Country | Country[], b: Alpha2CountryCode[]) => {
    if (!a) {
      return false;
    }
    
    return !doCountryCodesMatch.fn(a, b);
  },

  // TODO: [KONG] Update msg func to return something meaningful
  msg: () => {
    return '';
  },
};

const doAllCountryCodesMatch = {
  fn: (a: Country | Country[], b: Alpha2CountryCode[]) => {
    if (!a) {
      return false;
    }

    if (Array.isArray(a)) {
      return a
        .filter((country) => isCountry(country))
        .map(({ code }) => code)
        .every((countryCode) => {
          return b.includes(countryCode);
        });
    }

    if (isCountry(a)) {
      return b.includes(a.code);
    }

    return false;
  },

  // TODO: [Diogo] Update msg func to return something meaningful
  msg: () => {
    return '';
  },
};

const doAllCountryCodesNotMatch = {
  fn: (a: Country | Country[], b: Alpha2CountryCode[]) => {
    if (!a) {
      return false;
    }

    return !doAllCountryCodesMatch.fn(a, b);
  },

  // TODO: [Diogo] Update msg func to return something meaningful
  msg: () => {
    return '';
  },
};

const contains = {
  fn: <T extends unknown>(a: T[], b: T) => {
    if (!a || !Array.isArray(a)) {
      return false;
    }
    return a.includes(b);
  },
  msg: <T>(msg: string, value: T[], comparisonValue: T) => {
    const replacements: Record<string, string> = {
      '{{value}}': `[${value.join(', ')}]`,
      '{{comparisonValue}}': String(comparisonValue),
    };

    return msg.replace(/{{\w+}}/g, (key) => {
      return `${replacements[key]}` || key;
    });
  },
};

const notContains = {
  fn: <T extends unknown>(a: T[], b: T) => {
    return !contains.fn(a, b);
  },
  msg: <T>(msg: string, value: T[], comparisonValue: T) => {
    return contains.msg(msg, value, comparisonValue);
  },
};

const equalsOrContainsCountryCode = {
  fn: (a: Country | Country[], b: Alpha2CountryCode) => {
    if (!a) {
      return false;
    }

    if (Array.isArray(a)) {
      const countryCodes = a
        .filter((country) => isCountry(country))
        .map(({ code }) => code);

      return countryCodes.includes(b);
    }

    if (isCountry(a)) {
      return a.code === b;
    }

    return false;
  },

  // TODO: [KONG] Update msg func to return something meaningful
  msg: () => {
    return '';
  },
};

const notEqualOrContainsCountryCode = {
  fn: (a: Country | Country[], b: Alpha2CountryCode) => {
    if (!a) {
      return false;
    }

    return !equalsOrContainsCountryCode.fn(a, b);
  },

  // TODO: [KONG] Update msg func to return something meaningful
  msg: () => {
    return '';
  },
};

const always = {
  fn: <T extends unknown>(_a: T, _b: T) => {
    return true;
  },
  msg: <T>(msg: string, _value: T[], _comparisonValue: T) => {
    return msg;
  },
};

const isDefined = {
  fn: <T extends unknown>(a?: T) => {
    return a !== undefined;
  },
  msg: (msg: string) => {
    return msg;
  },
};

const isNotDefined = {
  fn: <T extends unknown>(a?: T) => {
    return a === undefined;
  },
  msg: (msg: string) => {
    return msg;
  },
};

export default {
  greaterThan,
  lessThan,
  dateIsInFuture,
  dateIsInPast,
  isIn,
  isNotIn,
  equals,
  notEquals,
  same,
  notSame,
  dateDiff,
  contains,
  notContains,
  equalsOrContainsCountryCode,
  notEqualOrContainsCountryCode,
  doCountryCodesMatch,
  doCountryCodesNotMatch,
  doAllCountryCodesMatch,
  doAllCountryCodesNotMatch,
  always,
  isDefined,
  isNotDefined,
};
